import axios from 'axios';

export const apiClient = axios.create({
  baseURL: 'https://api.suite.clasol.com/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isFlujosForbidden =
      error.response?.status === 403 &&
      originalRequest?.url?.includes('/flujos/') &&
      String(error.response?.data?.message || '').toLowerCase().includes('aplicacion');

    if (isFlujosForbidden && !originalRequest._flujosAccessRetry) {
      originalRequest._flujosAccessRetry = true;
      await apiClient.post('/auth/refresh');

      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export const login = async ({ login: loginValue, password }) => {
  const response = await apiClient.post('/auth/login', {
    login: loginValue,
    password,
  });

  return response.data;
};

export const getAuthenticatedUser = async () => {
  const response = await apiClient.get('/auth/me');

  return response.data;
};

export const refreshSession = async () => {
  const response = await apiClient.post('/auth/refresh');

  return response.data;
};

export const logout = async () => {
  await apiClient.post('/auth/logout');
};

export const getFlujosEntradas = async (params = {}) => {
  const response = await apiClient.get('/flujos/entradas', { params });

  return response.data;
};

const readFlujosListRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  return [];
};

const readTotalPages = (response) =>
  response?.meta?.totalPages ||
  response?.pagination?.totalPages ||
  response?.data?.meta?.totalPages ||
  response?.data?.pagination?.totalPages;

export const getAllFlujosEntradas = async (params = {}) => {
  const limit = Math.min(Math.max(Number(params.limit) || 500, 1), 500);
  const rows = [];
  const seenIds = new Set();

  for (let page = 1; page <= 100; page += 1) {
    const response = await getFlujosEntradas({ ...params, page, limit });
    const pageRows = readFlujosListRows(response);
    let newRows = 0;

    pageRows.forEach((row, index) => {
      const rowId = row?.id ?? row?.codigo ?? `${page}-${index}-${JSON.stringify(row)}`;

      if (!seenIds.has(rowId)) {
        seenIds.add(rowId);
        rows.push(row);
        newRows += 1;
      }
    });

    const totalPages = Number(readTotalPages(response));
    const reachedKnownEnd = Number.isFinite(totalPages) && totalPages > 0 && page >= totalPages;

    if (reachedKnownEnd || pageRows.length < limit || newRows === 0) {
      break;
    }
  }

  return rows;
};

export const getFlujosEntrada = async (id) => {
  const response = await apiClient.get(`/flujos/entradas/${id}`, {
    headers: { 'X-Application-Code': 'FLUJOS' },
  });

  return response.data;
};

export const createFlujosEntrada = async (entradaPayload) => {
  const response = await apiClient.post('/flujos/entradas', entradaPayload);

  return response.data;
};

export const updateFlujosEntrada = async (id, entradaPayload) => {
  const response = await apiClient.put(`/flujos/entradas/${id}`, entradaPayload, {
    headers: { 'X-Application-Code': 'FLUJOS' },
  });

  return response.data;
};

export const getFlujosEntradaHistorial = async (id, params = {}) => {
  const response = await apiClient.get(`/flujos/entradas/${id}/historial`, {
    params,
    headers: { 'X-Application-Code': 'FLUJOS' },
  });

  return response.data;
};

export const getFlujosVariedades = async () => {
  const response = await apiClient.get('/flujos/variedades');

  return response.data;
};

export const getFlujosVariedadesNav = async () => {
  const response = await apiClient.get('/flujos/variedades-nav', {
    headers: {
      'X-Application-Code': 'FLUJOS',
    },
  });

  return response.data;
};

export const getFlujosAlmacenes = async () => {
  const response = await apiClient.get('/flujos/almacenes');

  return response.data;
};

export const getFlujosPesosCajas = async () => {
  const response = await apiClient.get('/flujos/pesos-cajas');

  return response.data;
};

export const getFlujosPesosPalot = async () => {
  const response = await apiClient.get('/flujos/pesos-palot');

  return response.data;
};

export const getFlujosCajasPalet = async () => {
  const response = await apiClient.get('/flujos/cajas-palet');

  return response.data;
};

export const createFlujosPesoCaja = async (peso) => {
  const response = await apiClient.post('/flujos/pesos-cajas', { peso });

  return response.data;
};

export const ocultarFlujosPesoCaja = async (id) => {
  const response = await apiClient.post(`/flujos/pesos-cajas/${id}/ocultar`);

  return response.data;
};

export const restaurarFlujosPesoCaja = async (id) => {
  const response = await apiClient.post(`/flujos/pesos-cajas/${id}/restaurar`);

  return response.data;
};

export const createFlujosPesoPalot = async (peso) => {
  const response = await apiClient.post('/flujos/pesos-palot', { peso });

  return response.data;
};

export const ocultarFlujosPesoPalot = async (id) => {
  const response = await apiClient.post(`/flujos/pesos-palot/${id}/ocultar`);

  return response.data;
};

export const restaurarFlujosPesoPalot = async (id) => {
  const response = await apiClient.post(`/flujos/pesos-palot/${id}/restaurar`);

  return response.data;
};

export const createFlujosCajaPalet = async (cajas) => {
  const response = await apiClient.post('/flujos/cajas-palet', { cajas });

  return response.data;
};

export const ocultarFlujosCajaPalet = async (id) => {
  const response = await apiClient.post(`/flujos/cajas-palet/${id}/ocultar`);

  return response.data;
};

export const restaurarFlujosCajaPalet = async (id) => {
  const response = await apiClient.post(`/flujos/cajas-palet/${id}/restaurar`);

  return response.data;
};
